import { useState } from 'react'
import { parseAsInteger, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Button } from '@/components/ui/button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Plus, Search } from 'lucide-react'
import { useCustomerGroups, useCreateCustomerGroup } from '@/api/hooks'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { CustomersTable, CustomersTableSkeleton } from '@/components/customers-table'
import { toast } from 'sonner'
import { SearchInput } from '@/components/search-input'
import { ResultCount } from '@/components/list-filters'
import { resultCount } from '@/lib/utils'

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Newest customers', dir: 'desc' },
  { value: 'last_order_at', label: 'Last order', dir: 'desc' },
  { value: 'revenue', label: 'Revenue', dir: 'desc' },
  { value: 'name', label: 'Name', dir: 'asc' },
] as const

type CustomerSort = (typeof SORT_OPTIONS)[number]['value']

export function CustomerGroupsPage() {
  const [search, setSearch] = useUrlSearchText()
  const [{ sort: sortBy, page }, setFilters] = useQueryStates({
    sort: parseAsStringLiteral(SORT_OPTIONS.map((option) => option.value)).withDefault('created_at'),
    page: parseAsInteger.withDefault(1),
  })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newCompany, setNewCompany] = useState('')

  const { opsPersonId } = useMyProjectsFilter()
  const { data, isLoading } = useCustomerGroups({
    search: search || undefined,
    ops_person_id: opsPersonId,
    page,
    limit: 20,
    sort_by: sortBy,
    sort_dir: SORT_OPTIONS.find((option) => option.value === sortBy)?.dir,
  })
  const createGroup = useCreateCustomerGroup()

  const handleCreate = async () => {
    if (!newName.trim()) return
    try {
      await createGroup.mutateAsync({
        name: newName.trim(),
        company_name: newCompany.trim() || undefined,
      })
      toast.success('Customer group created')
      setNewName('')
      setNewCompany('')
      setDialogOpen(false)
    } catch {
      toast.error('Failed to create customer group')
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Customers</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Group
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Customer Group</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="name">Group Name</Label>
                <InlineFieldInput
                  id="name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter group name"
                />
              </div>
              <div>
                <Label htmlFor="company">Company Name</Label>
                <InlineFieldInput
                  id="company"
                  value={newCompany}
                  onChange={(e) => setNewCompany(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <Button onClick={handleCreate} disabled={createGroup.isPending}>
                Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <SearchInput
            placeholder="Search by name, company, or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              void setFilters({ page: 1 })
            }}
            className="pl-9"
          />
        </div>
        <Select
          value={sortBy}
          onValueChange={(v) => {
            void setFilters({ sort: v as CustomerSort, page: 1 })
          }}
        >
          <SelectTrigger aria-label="Sort customers" className="w-full md:w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ResultCount className="md:self-center">{resultCount(data?.total, 'customer')}</ResultCount>
      </div>

      {isLoading ? (
        <CustomersTableSkeleton />
      ) : data?.items?.length ? (
        <CustomersTable items={data.items} />
      ) : (
        <div className="py-12 text-center text-muted-foreground">No customer groups found</div>
      )}

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: page + 1 })}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
